import { useState, type ReactNode } from 'react';
import {
  Archive,
  ArrowRightLeft,
  BookOpen,
  ChartLine,
  CircleCheck,
  Clock,
  Compass,
  Globe,
  HardDrive,
  Import,
  KeyRound,
  Languages,
  Layers,
  LifeBuoy,
  Lock,
  Newspaper,
  Rocket,
  RotateCcw,
  SearchCheck,
  Server,
  ServerCrash,
  ShieldCheck,
  ShoppingCart,
  Trash2,
  TriangleAlert,
} from 'lucide-react';
import { Accordion, AccordionGroup, Card, PageHeader, TabPanel, Tabs } from '@/components/ui';

/**
 * Written from the screens as they exist, not from the plan (TZ section 15).
 *
 * It says plainly what does not work yet. A manual that describes features the
 * build does not have is worse than no manual: it sends an operator looking for
 * a button that is not there, during an incident.
 */

type Tab = 'now' | 'missing' | 'howto';

function Item({ done, children }: { readonly done: boolean; readonly children: ReactNode }): ReactNode {
  return (
    <li className="row">
      {done ? (
        <CircleCheck size={15} aria-hidden="true" className="icon-healthy" />
      ) : (
        <Clock size={15} aria-hidden="true" className="faint" />
      )}
      <span className={done ? '' : 'muted'}>{children}</span>
    </li>
  );
}

export function DocsPage(): ReactNode {
  const [tab, setTab] = useState<Tab>('now');

  return (
    <div className="page page-narrow">
      <PageHeader title="Справка" subtitle="Возможности, ограничения и порядок действий в типовых ситуациях" />

      <Tabs
        label="Разделы справки"
        active={tab}
        onChange={setTab}
        tabs={[
          { id: 'now', label: 'Возможности', icon: BookOpen },
          { id: 'missing', label: 'Ограничения', icon: Clock },
          { id: 'howto', label: 'Что делать, если', icon: LifeBuoy },
        ]}
      />

      <TabPanel id="now" active={tab}>
        <div className="stack">
          <Card title="Сайты" icon={Compass}>
            <ul className="stack stack-tight list-plain">
              <Item done>Список сайтов: поиск, фильтры, теги, вид таблицей или карточками</Item>
              <Item done>Новый сайт в одно нажатие: шаблон, название, страна флагом и домен — панель сама покупает домен, настраивает DNS и создаёт сайт</Item>
              <Item done>Сайт из файлов на внешнем сервере и пустой WordPress</Item>
              <Item done>Шаблоны со структурой: правка структуры в шаблоне — на всех его сайтах</Item>
              <Item done>Клонирование: тестовая копия или новый сайт на своём домене, со страной и языком</Item>
              <Item done>Импорт WordPress с другого сервера с проверкой до переключения DNS</Item>
              <Item done>Мониторинг внешних сайтов без управления ими</Item>
              <Item done>Перезапуск, развёртывание, обновление WordPress/PHP, перенос, удаление</Item>
              <Item done>Резервные копии по расписанию и вручную, восстановление, развёртывание копии на другом сервере</Item>
              <Item done>Восстановление на другом сервере, если сервер сайта недоступен</Item>
              <Item done>Домены сайта, DNS-записи, SSL, редиректы и кэш Cloudflare</Item>
              <Item done>Хранилище доступов и вход в админку CMS из панели</Item>
              <Item done>Массовые действия: SEO-проверка, снимки, платформа, Search Console, SEO-данные, копии, перезапуск, обновление до версии шаблона</Item>
              <Item done>Проверка доменов перед покупкой и покупка через 2d2s</Item>
              <Item done>Автоконтент: темы и статьи от Claude с опорой на источники, публикация в WordPress</Item>
            </ul>
          </Card>

          <Card title="Мониторинг" icon={ShieldCheck}>
            <ul className="stack stack-tight list-plain">
              <Item done>Доступность: снаружи каждые 5 минут, с сервера сайта каждую минуту</Item>
              <Item done>SEO раз в сутки: индексация, robots.txt, sitemap, мета-теги, язык, hreflang, ключевые страницы</Item>
              <Item done>Google Search Console: клики, показы, позиции, запросы, индексация страниц, sitemap</Item>
              <Item done>Показатели доменов: Web Archive, Ahrefs DR, Open PageRank, DataForSEO — раз в неделю</Item>
              <Item done>Динамика SEO: клики и показы за 16 месяцев, история DR, ссылок и трафика из Ahrefs за 5 лет</Item>
              <Item done>Интеграции проверяются сами каждые 6 часов; у каждой своя страница со статусом и историей</Item>
              <Item done>Снимки главных страниц</Item>
              <Item done>Алерты на главной и в Telegram, с уведомлением о решении; алерт, если главной нет в индексе Google</Item>
              <Item done>Еженедельная проверка восстановления каждого сайта</Item>
              <Item done>Сертификат сервера и режим SSL зоны Cloudflare</Item>
            </ul>
          </Card>

          <Card title="Доступ и инфраструктура" icon={Server}>
            <ul className="stack stack-tight list-plain">
              <Item done>Вход по паролю и коду из приложения-аутентификатора; роли сотрудников</Item>
              <Item done>Журнал действий только для чтения</Item>
              <Item done>Подключение серверов: агент устанавливается по SSH или командой, обновляется сам</Item>
              <Item done>Страница сервера: ресурсы, агент, сайты, их копии, доступы и журнал</Item>
              <Item done>Доступы серверов (SSH, SFTP, панель хостинга, база) — только для просмотра, каждый показ пароля в журнале</Item>
              <Item done>Закрытие сервера для новых сайтов и перенос всех сайтов с него</Item>
              <Item done>Зашифрованные копии на диске панели или в S3-совместимом хранилище</Item>
              <Item done>Копии по слотам: до N на сайт, новая после проверки заменяет самую старую; страница копий по серверам с поиском</Item>
              <Item done>Ночная копия базы панели</Item>
              <Item done>Очистка: задачи — через 7 дней, журнал — через год, копии — по политике сайта</Item>
            </ul>
          </Card>

          <p className="field-hint">Флаги стран — Twemoji, © Twitter, Inc. и авторы, лицензия CC-BY 4.0.</p>
        </div>
      </TabPanel>

      <TabPanel id="missing" active={tab}>
        <Card title="Ограничения" icon={Clock}>
          <ul className="stack stack-tight list-plain">
            <Item done={false}>Внешнее S3-хранилище не подключено: копии хранятся на серверах сайтов и панели</Item>
            <Item done={false}>Отдельный SMTP для каждого сайта: почта всех сайтов идёт через релей панели</Item>
            <Item done={false}>Автоконтент публикует только в WordPress; картинки к статьям не создаются</Item>
            <Item done={false}>Отправка страниц на индексацию: Google принимает только sitemap</Item>
            <Item done={false}>Версии плагинов WordPress и их обновление из панели</Item>
            <Item done={false}>Массовые удаление, восстановление и перенос</Item>
            <Item done={false}>Удаление сервера из панели</Item>
            <Item done={false}>Управление сайтами на Mini CMS</Item>
            <Item done={false}>Новый сайт в одно нажатие — пока только WordPress из шаблона; лендинги из архива — следующим шагом</Item>
            <Item done={false}>Цену 2d2s сообщает для имени, которое выбрал сам; для другого свободного имени она может отличаться</Item>
          </ul>
        </Card>
      </TabPanel>

      <TabPanel id="howto" active={tab}>
        <AccordionGroup>
          <Accordion title="Создать новый сайт" icon={Rocket}>
            <ol className="stack stack-tight">
              <li>«Сайты» → «Новый сайт» → «Сайт на WordPress из шаблона».</li>
              <li>Шаблон, название сайта и страна с языком — по флагу. Язык, часовой пояс и форматы даты задаёт страна.</li>
              <li>
                Домен: «Купить новый» — панель предложит имена из названия и страны, «Проверить доступность» покажет свободные;
                или «Из моих доменов». Проверка бесплатна.
              </li>
              <li>
                «Проверка»: сводка, цена по расчёту 2d2s и баланс. «Купить домен и создать сайт — $X» оплачивает домен сразу,
                отменить после этого нельзя.
              </li>
              <li>
                Дальше панель всё делает сама: покупка, зона Cloudflare, домен становится доступен панели, сервер, WordPress,
                шаблон и язык, DNS, проверка. Окно можно закрыть — на странице «Сайты» остаётся полоса «В работе» с «Следить».
              </li>
              <li>
                Если ответа 2d2s об оплате нет, повторной оплаты не будет: заказ ждёт 15 минут и останавливается. Если заказ
                остановился после покупки, домен остаётся в 2d2s — сайт на нём можно создать из «Моих доменов».
              </li>
              <li>Для покупки в 2d2s должен быть задан тег доменов панели (TWOD2S_DOMAIN_TAGS): без него панель не настроит DNS.</li>
            </ol>
          </Accordion>

          <Accordion title="Запустить новое ГЕО" icon={Languages}>
            <ol className="stack stack-tight">
              <li>Создайте сайт, как описано выше. Сайт создаётся закрытым от поисковиков; пароль администратора сразу лежит в «Доступах».</li>
              <li>
                Страница сайта → «Запуск» → «Перевести»: режим «Списком» копирует все строки разом — переведите их в
                любом переводчике и вставьте обратно.
              </li>
              <li>Проверьте страницы по ссылкам и нажмите «Открыть» — сайт откроется для индексации.</li>
            </ol>
          </Accordion>

          <Accordion title="Подключить Google Search Console" icon={SearchCheck}>
            <ol className="stack stack-tight">
              <li>
                В Google Cloud Console создайте проект, включите Google Search Console API и Site Verification API, создайте
                сервисный аккаунт и JSON-ключ к нему.
              </li>
              <li>«Интеграции» → Google Search Console: вставьте ключ, при желании — свой Google-аккаунт владельцем.</li>
              <li>
                Страница сайта → SEO → «Подключить», или несколько сайтов разом из списка. Запись TXT панель создаёт сама, где
                может менять DNS домена; иначе покажет, какую запись добавить.
              </li>
              <li>После подтверждения sitemap отправляется сам, данные обновляются раз в сутки. Новое ГЕО подключается при запуске.</li>
            </ol>
          </Accordion>

          <Accordion title="Настроить автоконтент" icon={Newspaper}>
            <ol className="stack stack-tight">
              <li>«Интеграции» → Claude: API-ключ, модель и бюджет в месяц.</li>
              <li>
                В WordPress: «Пользователи» → профиль → «Пароли приложений» → создайте пароль. В панели: «Доступы» сайта →
                доступ вида «CMS» с этим логином и паролем.
              </li>
              <li>Вкладка «Контент» сайта → «Настроить»: тематика, язык, режим и доступ к CMS. «Проверить доступ» покажет, можно ли публиковать.</li>
              <li>
                «Предложить темы» или «Своя тема», затем «Написать». Режим решает, что дальше: статья ждёт проверки в панели,
                уходит в WordPress черновиком или публикуется по графику. Автопилот сам поддерживает нужное число статей в неделю.
              </li>
            </ol>
          </Accordion>

          <Accordion title="Проверить домен перед покупкой" icon={Globe}>
            <ol className="stack stack-tight">
              <li>«Домены» → «Проверка доменов»: до 20 имён за раз, строки заполняются по мере ответа источников.</li>
              <li>
                Перерыв в истории Web Archive часто означает смену владельца, высокий спам-скор — плохие ссылки. Это повод
                открыть снимки в архиве, а не окончательный приговор.
              </li>
            </ol>
          </Accordion>

          <Accordion title="Купить домены" icon={ShoppingCart}>
            <ol className="stack stack-tight">
              <li>«Домены» → «Купить домены»: имена, сколько купить, срок. На этом шаге денег не тратится.</li>
              <li>2d2s проверяет доступность — видно, какие имена свободны. «Проверить историю свободных» покажет архив, DR и спам.</li>
              <li>Отметьте имена и нажмите «Купить»: подтверждение со списком и ценой, оплата только после него.</li>
              <li>
                Мастер показывает оплату и настройку DNS по каждому домену и обновляется каждые 2 секунды. Окно можно закрыть:
                покупка продолжится, в списке — «Следить», в «Ходе покупки» — что и когда произошло.
              </li>
              <li>В конце — «Создать сайт на …»: мастер нового сайта откроется с этим доменом.</li>
            </ol>
          </Accordion>

          <Accordion title="Подключить Ahrefs и смотреть динамику" icon={ChartLine}>
            <ol className="stack stack-tight">
              <li>Ahrefs → Account settings → API keys → Generate. «Интеграции» → Ahrefs: вставьте ключ.</li>
              <li>Бесплатный ключ даёт текущий DR. История по месяцам — с тарифа Lite; ограничьте расход ключа в самом Ahrefs.</li>
              <li>Страница сайта → SEO → «Динамика»: клики и показы из Search Console, DR, ссылающиеся домены и трафик.</li>
            </ol>
          </Accordion>

          <Accordion title="Изменить структуру на всех сайтах шаблона" icon={Layers}>
            <ol className="stack stack-tight">
              <li>Меняйте тему, плагины, меню, рубрики и настройки в админке эталона — его видно в карточке шаблона.</li>
              <li>«Шаблоны» → шаблон → «Опубликовать изменения эталона»: появится новая версия со списком изменений.</li>
              <li>
                «Обновить до …»: сначала обновится один сайт, потом остальные по два. Перед каждым — копия; при сбое сайт
                вернётся к прежней версии.
              </li>
              <li>Переводы строк у сайтов сохраняются; новые строки останутся на языке шаблона, пока их не переведут.</li>
            </ol>
          </Accordion>

          <Accordion title="Сайт не отвечает" icon={TriangleAlert}>
            <ol className="stack stack-tight">
              <li>«Серверы»: проверьте отклик агента. Если сервер не отвечает, причина в сервере, а не в сайте.</li>
              <li>Страница сайта → «История»: последние операции, в том числе незавершённые.</li>
              <li>Состояние «Нет данных» не означает отказ: свежих проверок нет, но сайт может работать.</li>
            </ol>
          </Accordion>

          <Accordion title="Домен указывает не туда" icon={Globe}>
            <ol className="stack stack-tight">
              <li>Страница сайта → «Домены»: колонки «Должен указывать на» и «Указывает на» показывают расхождение.</li>
              <li>
                Для доменов с разрешённым тегом 2d2s записи меняются автоматически. Для остальных — вручную в 2d2s,
                состояние домена «Ожидает».
              </li>
              <li>При удалении сайта удаляются только записи, созданные панелью.</li>
            </ol>
          </Accordion>

          <Accordion title="Подключить новый сервер" icon={Server}>
            <ol className="stack stack-tight">
              <li>«Серверы» → «Подключить сервер», выберите тип.</li>
              <li>
                Сервер для сайтов панели: поставьте серверу в 2d2s тег, указанный в окне, затем «Обновить из 2d2s» и
                выберите сервер.
              </li>
              <li>Внешний сервер с сайтами: укажите название и IP-адрес.</li>
              <li>Установите агента: по SSH (root и пароль или ключ) или командой вручную — токен действует час.</li>
              <li>Когда агент подключится, состояние сервера сменится на «Активен».</li>
            </ol>
          </Accordion>

          <Accordion title="Перенести сайт на другой сервер" icon={ArrowRightLeft}>
            <ol className="stack stack-tight">
              <li>Страница сайта → «Ещё» → «Перенести». Нужна роль администратора или владельца.</li>
              <li>Во время переноса (обычно несколько минут) сайт показывает страницу обслуживания.</li>
              <li>При сбое сайт автоматически откроется на прежнем сервере.</li>
              <li>После проверки удалите остановленную копию: страница сайта → «Остановленные копии» → «Удалить копию».</li>
            </ol>
          </Accordion>

          <Accordion title="Хранить доступы к серверу" icon={KeyRound}>
            <ol className="stack stack-tight">
              <li>«Серверы» → сервер → «Доступы» → «Добавить»: SSH, SFTP, панель хостинга, база данных.</li>
              <li>Панель по этим доступам на сервер не заходит: она работает через агента. Доступы — для людей.</li>
              <li>Пароль зашифрован; показ и копирование доступны владельцу и администраторам и записываются в журнал сервера.</li>
            </ol>
          </Accordion>

          <Accordion title="Освободить сервер" icon={HardDrive}>
            <ol className="stack stack-tight">
              <li>«Серверы» → сервер → «Обзор» → «Перенести все сайты», введите имя сервера.</li>
              <li>Каждый сайт переносится отдельной задачей; сайт, который перенести не удалось, продолжает работать.</li>
              <li>После переноса последнего сайта сервер закрывается для новых сайтов.</li>
            </ol>
          </Accordion>

          <Accordion title="Сервер сайта недоступен" icon={ServerCrash}>
            <ol className="stack stack-tight">
              <li>Подождите 1–2 минуты: после перезагрузки агент подключается сам. Проверьте сервер в 2d2s.</li>
              <li>
                Страница сайта → «Восстановить на другом сервере». Изменения после даты копии будут потеряны — дата
                указана в окне.
              </li>
              <li>Когда сервер вернётся, копии сайтов на нём будут остановлены, но не удалены.</li>
            </ol>
          </Accordion>

          <Accordion title="Перевести WordPress под управление панели" icon={Import}>
            <ol className="stack stack-tight">
              <li>«Сайты» → «Подключить» → «Импорт WordPress». Старый сервер продолжает работать.</li>
              <li>Страница сайта → «Получить команду для старого сервера», выполните её на старом сервере.</li>
              <li>
                Проверьте сайт через файл hosts, затем «Досинхронизировать» и «Переключить DNS на панель». Вернуть DNS можно
                кнопкой «Вернуть DNS на старый сервер».
              </li>
            </ol>
          </Accordion>

          <Accordion title="«Flexible» в колонке «SSL зоны»" icon={Lock}>
            <ol className="stack stack-tight">
              <li>В режиме Flexible Cloudflare обращается к серверу по HTTP, сервер перенаправляет на HTTPS — возникает цикл.</li>
              <li>Переключите режим SSL зоны в 2d2s: Full (strict) для проверяемого сертификата, Full — для самоподписанного.</li>
            </ol>
          </Accordion>

          <Accordion title="Сайт не создался" icon={TriangleAlert}>
            <ol className="stack stack-tight">
              <li>Причина указана на странице сайта.</li>
              <li>Устраните причину и удалите сайт — копия для этого не нужна.</li>
              <li>Создайте сайт заново с другим идентификатором: идентификатор удалённого сайта остаётся занятым.</li>
            </ol>
          </Accordion>

          <Accordion title="Вернуть сайт к копии" icon={RotateCcw}>
            <ol className="stack stack-tight">
              <li>Страница сайта → «Копии» → «Восстановить». Доступны только проверенные копии.</li>
              <li>Перед восстановлением создаётся копия текущего состояния.</li>
              <li>Если проверка после восстановления не прошла, сайт остаётся на обслуживании.</li>
            </ol>
          </Accordion>

          <Accordion title="Сколько копий хранится" icon={Archive}>
            <ol className="stack stack-tight">
              <li>«Резервные копии» → «Копий на сайт»: от 1 до 10, по умолчанию 3. Слоты — «Копия 1», «Копия 2»…</li>
              <li>Новая копия сначала проверяется, затем заменяет самую старую и получает её номер. Если новая не удалась, старые остаются.</li>
              <li>Копии перед восстановлением и переносом хранятся неделю сверх слотов.</li>
              <li>Когда на диске панели остаётся меньше 5 ГБ, новые копии не принимаются: подключите S3.</li>
            </ol>
          </Accordion>

          <Accordion title="Удалить сайт" icon={Trash2}>
            <ol className="stack stack-tight">
              <li>Удаление доступно администратору и владельцу.</li>
              <li>Нужна копия, проверенная восстановлением. Если её нет, создайте копию и дождитесь проверки.</li>
              <li>Для подтверждения введите идентификатор сайта.</li>
            </ol>
          </Accordion>
        </AccordionGroup>
      </TabPanel>
    </div>
  );
}
